import { Component, ViewEncapsulation, Input, ViewChild, InjectionToken, Inject, AfterViewInit, Output, EventEmitter, TemplateRef, ContentChild, ContentChildren, AfterContentInit, SimpleChanges, ElementRef } from '@angular/core';
import { CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { AutoSub, AutoSubOnDestroy } from '@motes-sdk/providers';
import { debounceTime, repeat, tap, distinctUntilChanged, switchMap, withLatestFrom, takeWhile, throttleTime, first, takeUntil } from 'rxjs/operators';
import { SwiperScrollStrategy } from './swiper-scroll-strategy';
import { QueryList } from '@angular/core';
import { Slide, SwiperOrientation, SwiperSlideDirective, SwiperSlideTemplateDirective } from './virtual-swiper-model';
import { Observable, animationFrameScheduler, ReplaySubject, Subject, fromEvent, interval } from 'rxjs';
import { DeviceService } from '@motes-sdk/providers/device.service';

export const VIRTUAL_SCROLL_STRATEGY_CONFIG = new InjectionToken<number>('SWIPER_STRATEGY_CONFIG');

@AutoSubOnDestroy()
@Component({
    selector: 'virtual-swiper',
    templateUrl: './virtual-swiper.component.html',
    styleUrls: ['./virtual-swiper.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: VIRTUAL_SCROLL_STRATEGY,
            useClass: SwiperScrollStrategy
        }
    ]
})
export class VirtualSwiperComponent implements AfterViewInit, AfterContentInit {
    @Input('slides') set setSlides(slides: Slide[]) {
        this.slidesInputEnabled = true;
        this.slides = slides;
    }

    @Input() slideSize: number = 460;
    @Input() slideMargin: number = 0;
    @Input() swiperHeight: number = -1;
    @Input() showPagination: boolean = false;
    @Input() slideSnapStop: boolean = true;
    @Input() slideTemplate: TemplateRef<any> = null;
    @Input() orientation: SwiperOrientation = 'horizontal';
    @Input() selectSlideOnClick: boolean = true;
    @Input() emulateTouchMultiSwipe: boolean = false;
    @Input() immediateKeyChangeEmission: boolean = false;
    @Input() changeActiveDuringTouch: boolean = true;

    @Input() activeKey: string = "";
    @Output() activeKeyChanged = new EventEmitter<string>();
    @Output() activeSlideChanged = new EventEmitter<Slide>();

    // Max animation of each round of animatin (when key is pressed down)
    @Input() maxSlideTime: number = 450;
    @Input() slideTime: number = 350;

    // View buffer settings
    @Input() bufferMinSlides: number = 4;
    @Input() bufferMaxSlides: number = 10;
    // @Input() takeFocusOnKeyChange: boolean = false;
    @Input() enableKeyboardShortcuts: boolean = false;
    // Control output
    @Output() slideSelected = new EventEmitter<Slide>();
    @Output() sliderEscape = new EventEmitter<void>();

    @ViewChild("virtualSwiper", { static: true }) container: ElementRef<HTMLDivElement>;
    @ViewChild(CdkVirtualScrollViewport, { static: true }) slider: CdkVirtualScrollViewport;
    // Projected content children list
    @ContentChildren(SwiperSlideDirective) contentSlides: QueryList<SwiperSlideDirective>;
    // Projected content template
    @ContentChild(SwiperSlideTemplateDirective, { static: true, read: TemplateRef }) contentTemplate: TemplateRef<any>;

    // Current active slide index
    public activeIdx: number = 0;
    // Target scroll slide index
    public targetIdx: number = 0;
    public slides: Slide[] = [];

    private slidesInputEnabled = false;

    private targetIdxSubject: ReplaySubject<{ idx: number, speed?: number, keyboardChange?: boolean }> = new ReplaySubject(1);
    private keyDownSubject: Subject<'prev' | 'next'> = new Subject();
    private touchEndSubject: Subject<Event> = new Subject();

    private ongoingTouch = true;
    private touchTargetIdx = 0;
    private touchStartOffset = 0;
    private touchStartTime = 0;
    // Debounce time used to determine when the swiper has settled (reached the target slide)
    // This value should be big enough to avoid multiple index output change events, however
    // at the same time small enough that user are able to select a new mote in between
    private readonly SLIDE_CHANGE_DEBOUNCE_TIME: number = 120;

    // The number of milliseconds that the animation is allowed to run without reaching its target scroll position
    // This is useful for reaching the correct target slide on devices that lacks (especially on iOS with many slides)
    private readonly MAX_ANIMATION_LACK_TIME: number = 100;

    // Inject ref to virtual scroll strategy in component to config item size and more
    constructor(@Inject(VIRTUAL_SCROLL_STRATEGY) private strategy: SwiperScrollStrategy, private deviceService: DeviceService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes.slideSize) this.updateSlideSize();
        if(changes.marginSize) this.updateSlideSize();
        if(changes.swiperHeight) this.updateSlideSize();
        if(changes.slideSnapStop) this.enableSnappingStop(changes.slideSnapStop.currentValue);
        if(changes.activeKey) this.updateActiveKey(changes.activeKey.currentValue);
    }

    ngAfterContentInit() {
        // Subscribe to dynamic view updates in parent
        AutoSub(this).reg['slideChanges'] = this.contentSlides.changes.subscribe((slides) => {
            this.loadProjectedContent();
        })

        AutoSub(this).reg['keyupSub'] = fromEvent<KeyboardEvent>(window, 'keyup', { passive: false }).subscribe((ev) => {
            this.onKeyUp(ev);
        })

        AutoSub(this).reg['keydownSub'] = fromEvent<KeyboardEvent>(window, 'keydown', { passive: false }).subscribe((ev) => {
            this.onKeyDown(ev);
        })

        // Load projected content before generating view
        this.loadProjectedContent();
    }

    ngAfterViewInit() {
        if(this.deviceService.isPlatformServer()) return;

        // Subscribe to slide index changes on scroll (this will fire _after_ scrolling has settled)
        let afterScrollIdxChanged = this.slider.scrolledIndexChange.pipe(debounceTime(this.SLIDE_CHANGE_DEBOUNCE_TIME), distinctUntilChanged());
        AutoSub(this).reg['scrollStopSub'] = afterScrollIdxChanged.subscribe((idx) => {
            if(!this.ongoingTouch || this.changeActiveDuringTouch) {
                this.setActiveIdx(idx);
                this.emitActiveIdx(idx);
            }
        });

        // let afterChangeAndTouchEnded = this.touchEndSubject.pipe(switchMap(() => afterScrollIdxChanged.pipe(first())));
        // AutoSub(this).reg['scrollTouchEndSub'] = afterChangeAndTouchEnded.subscribe((idx) => {
        //     let ongoingAuto = (this.targetIdx != this.activeIdx);
        //     if(this.ongoingTouch) return;
        //     this.setActiveIdx(idx);
        //     this.emitActiveIdx(idx);
        // });

        // Subscribe to slide index changes on scroll (this will fire _before_ scroll has settled)
        let debounceBeforeSettled = this.immediateKeyChangeEmission ? 0 : 150;
        AutoSub(this).reg['indexChangeSub'] = this.slider.scrolledIndexChange.pipe(distinctUntilChanged(), debounceTime(debounceBeforeSettled)).subscribe((idx) => {
            // Highlight active a bit before scroll has settled
            this.setActiveIdx(idx);
            let ongoingAuto = (this.targetIdx != this.activeIdx);
            // Emit index changed event
            if((ongoingAuto || (this.ongoingTouch && !this.immediateKeyChangeEmission))) return;
            this.emitActiveIdx(idx);
        });

        // Subscribe to key events and fast scroll slider when key is down
        AutoSub(this).reg['keyDownSub'] = this.keyDownSubject.pipe(throttleTime(30)).subscribe((action) => {
            if(action == 'next') this.targetIdx++;
            if(action == 'prev') this.targetIdx--;
            // Clamp target value to start and end
            this.targetIdx = (this.targetIdx < 0) ? 0 : this.targetIdx;
            this.targetIdx = (this.targetIdx >= this.slides.length - 1) ? this.slides.length - 1 : this.targetIdx;
            // Enable fast sliding when key is down
            let slideDiff = Math.abs(this.activeIdx - this.targetIdx);
            // Increase speed the longer target is away from active
            let speed = slideDiff > 2 ? Math.round(this.slideTime / 3) : this.slideTime - (this.slideTime / 5);
            this.targetIdxSubject.next({ idx: this.targetIdx, speed: speed, keyboardChange: true });
        });

        // Watch scroll target subject
        AutoSub(this).reg['scrollTarget'] = this.watchScrollAnimation().subscribe();
        AutoSub(this).reg['mouseupSub'] = fromEvent<MouseEvent>(window, 'mouseup', { passive: true }).subscribe((ev) => this.onMouseUp(ev));
        AutoSub(this).reg['touchendSub'] = fromEvent<UIEvent>(window, 'touchend', { passive: true }).subscribe((ev) => this.onTouchEnd(ev));

        this.updateSlideSize();
    }


    private emitActiveIdx(idx: number) {
        // console.log("Emit active slide idx " + idx);
        if(!this.slides || this.slides.length < idx || !this.slides[idx]) return;
        this.activeKeyChanged.emit(this.slides[idx].key);
        this.activeSlideChanged.emit(this.slides[idx]);
    }

    private updateSlideSize() {
        if(this.deviceService.isPlatformServer()) return;

        // Set scss slide size and swiper height vars from component input
        this.container.nativeElement.style.setProperty('--slide-size', this.slideSize + 'px');
        this.container.nativeElement.style.setProperty('--slide-margin', this.slideMargin + 'px');
        // Only set swiper height if defined outside, otherwise rely on pure css defined height
        if(this.swiperHeight > 0) this.container.nativeElement.style.setProperty('--swiper-height', this.swiperHeight + 'px');

        let slideSizeMargin = this.slideSize + this.slideMargin * 2;
        // Initialize fixed slide size from input
        let minBuffer = slideSizeMargin * this.bufferMinSlides;
        let maxBuffer = slideSizeMargin * this.bufferMaxSlides;
        // Apply slide size and buffer options to scroll strategy 
        this.strategy.init({ itemSize: slideSizeMargin, minBufferPx: minBuffer, maxBufferPx: maxBuffer });

        // Init active idx if activeKey is provided from inpmaxSlideTimeut
        if(this.activeKey) this.activeIdx = this.getSlideKeyIdx(this.activeKey);

        // Adjust scroll to new size
        this.scrollToOffset(this.getIdxOffset(this.activeIdx));
    }

    private updateActiveKey(key: string) {
        if(!this.slider) {
            console.warn("Cant scroll to key as swiper is not initialized yet ", key);
            return;
        }
        let idx = this.getSlideKeyIdx(key);
        if(idx < 0) return;
        this.targetIdxSubject.next({ idx: idx });
    }

    private loadProjectedContent() {
        // Load template from swiper content if any
        if(this.contentTemplate) this.slideTemplate = this.contentTemplate;
        // Load slide templates from swiper content if any
        if(!this.contentSlides) return;
        // Slides where given as argument, so don't use projected
        if(this.slidesInputEnabled) return;
        // if(this.slides.length > 0) return;

        // Get content children as array
        let projectedSlides = this.contentSlides.toArray();

        // Overwrite slides from input if content is projected (we won't show both slides from input and projection)
        this.slides = projectedSlides.map((slide, idx): Slide => {
            // Get key from slide directive
            let key = slide.options.key || `slide-${idx}`;
            let doShow = (slide.options.doShow !== undefined && !slide.options.doShow) ? false : true;
            // Return Slide instance with template from directive
            return { key: key, templateRef: slide.templateRef, hidePagination: slide.options.hidePagination, doShow: doShow };
        })
    }

    public scrollToSlideKey(slideKey: string): boolean {
        // Find position of key in list
        let slideIdx = this.getSlideKeyIdx(slideKey);

        if(slideIdx < 0) return false;

        this.slideToIdx(slideIdx);
        return true;
    }

    public slideToIdx(idx: number) {
        this.targetIdxSubject.next({ idx: idx });
    }

    private getSlideKeyIdx(key: string) {
        return this.slides.findIndex(slide => slide.key == key);
    }

    private getIdxOffset(idx: number): number {
        return Math.round(idx * (this.slideSize + (this.slideMargin * 2)));
    }

    private scrollToOffset(itemOffset: number) {
        this.slider.scrollToOffset(itemOffset, 'auto');
    }

    private scrolleToIdx(itemIdx: number) {
        this.scrollToOffset(this.getIdxOffset(itemIdx));
    }

    public slideToNext() {
        let nextIdx = this.activeIdx + 1;
        if(nextIdx >= this.slides.length) return;
        this.slideToIdx(nextIdx);
    }

    public slideToPrev() {
        let prevIdx = this.activeIdx - 1;
        if(prevIdx < 0) return;
        this.slideToIdx(prevIdx);
    }

    private setActiveIdx(idx: number) {
        if(this.activeIdx == idx) return;

        this.activeIdx = idx;
    }

    public onKeyUp(ev: KeyboardEvent) {
        if(!this.enableKeyboardShortcuts) return;
        ev.preventDefault();
    }

    public onKeyDown(ev: KeyboardEvent) {
        if(!this.enableKeyboardShortcuts) return;

        switch(ev.code) {
            case "ArrowLeft":
                // Slide to previous if we are in horizontal mode
                if(this.orientation != 'horizontal') break;
                // Prevent default scroll of div
                ev.preventDefault();
                this.keyDownSubject.next('prev');
                break

            case "ArrowRight":
                // Slide to next if we are in horizontal mode
                if(this.orientation != 'horizontal') break;
                // Prevent default scroll of div
                ev.preventDefault();
                this.keyDownSubject.next('next');
                break;

            case "ArrowUp":
                // Slide to next if we are in horizontal mode
                if(this.orientation != 'vertical') break;
                // Prevent default scroll of div
                ev.preventDefault();
                this.keyDownSubject.next('next');
                break;

            case "ArrowDown":
                // Slide to next if we are in horizontal mode
                if(this.orientation != 'vertical') break;
                // Prevent default scroll of div
                ev.preventDefault();
                this.keyDownSubject.next('prev');
                break;

            case "Enter":
                // Emit slide to be selected signal on enter press to be handled by parent
                let slide = this.activeIdx < this.slides.length ? this.slides[this.activeIdx] : null;
                this.slideSelected.emit(slide);
                break;

            case "Escape":
                // Emit close signal when escape pressed to be handled by parent
                this.sliderEscape.emit();
                break;
        }
    }

    /**
    * Synchonize keyboard scroll events with onAnimFrame to get more smooth keyboard scroll animation
    */
    private watchScrollAnimation(): Observable<any> {
        let currentOffset = 0;
        let targetOffset = -1;
        let duration = -1;
        let distance = 0;
        let startTime = 0;
        let currentTime = 0;
        let startOffset = 0;
        let slideCount = 0;

        const stopAnimation = new Subject<void>();

        // Watch target idx and init animation 
        let watchTargetIdx = this.targetIdxSubject.pipe(tap((ev) => {
            // Cancel any ongoing animations (this does not in reality have any effect, but could and should stop ongoing animations in rare cases)
            stopAnimation.next();

            let idx = ev.idx;

            // Update the target idx (used when key is down to increment or decrement target)
            this.targetIdx = idx;
            // Calculate distance to target offset from current offset to adjust animation time
            currentOffset = this.slider.measureScrollOffset();
            targetOffset = this.getIdxOffset(idx);

            // Early out if scroll position is already as it should be
            if(targetOffset == currentOffset) return;
            // Init animation by disabling snapping (otherwise we cant programatically control the scroll)
            this.disableSnapping();

            // Calculate animation time depending on how many slides to scroll through
            slideCount = Math.round(Math.abs(currentOffset - targetOffset) / this.slideSize) || 1;
            // Immediately activate slide to trigger slide resize before scrolling when scrolling multiple slides
            if(slideCount > 1 && !ev.keyboardChange) this.activeIdx = idx;

            duration = ev.speed ? ev.speed : this.slideTime * slideCount;
            // Clamp multi animation time to max
            duration = (slideCount > 1 && duration > this.maxSlideTime) ? this.maxSlideTime : duration;

            // Detect long animations (animation bigger than ~4 slides) in order to avoid performance issues in long scrolling lists
            let clampLongAnimation = (slideCount > this.bufferMinSlides);
            // Find the direction to scroll in
            let direction = Math.sign(targetOffset - startOffset);
            // Calculate the offset from the target position to use in case we need to clamp the animation (animate only 3 slides up to target)
            let clampOffset = (this.slideSize * 3) * direction;

            // Clamp start slide position if this is a very long animation or just animate from the current position
            startOffset = (clampLongAnimation) ? (targetOffset - clampOffset) : currentOffset

            // Calc distance to target item
            distance = (targetOffset - startOffset);
            startTime = performance.now();
            currentTime = startTime;
        }));

        // Watch animation until target scroll has been reached
        let watchAnimationDone = takeWhile(() => {
            currentOffset = this.slider.measureScrollOffset();
            currentTime = performance.now();

            // Animate until target scroll has been reached (and limit animation time to ~5 sec)
            let timeLeft = duration - (currentTime - startTime);

            // If animation is done (exceeded max time with more than MAX_ANIMATION_LACK_TIME ms), make sure that scroll is set to target offset,
            // in case it lacks - which can be the case with big collections on iOS
            let animationDone = (targetOffset == currentOffset) || timeLeft <= -this.MAX_ANIMATION_LACK_TIME;

            // Re enable snapping to items when scroll animation is done
            if(animationDone) this.enableSnapping();
            // OBS: Focus will immediately scroll to slide
            // if(animationDone) this.slider.elementRef.nativeElement.focus();
            return !animationDone;
        });

        // Watch animation frames until animation is done - then resubscribe to wait for next target idx
        let watchAnimFrame = interval(0, animationFrameScheduler).pipe(repeat(), watchAnimationDone, takeUntil(stopAnimation));

        // Switch animation observable on index change
        let animateToIdx = watchTargetIdx.pipe(switchMap((idx) => watchAnimFrame));

        let smoothScroll = animateToIdx.pipe(withLatestFrom(watchTargetIdx, (move, targetIdx) => {
            // Calculate next step in animation
            let nextOffset = this.interpolateScroll(startOffset, targetOffset, startTime, currentTime, duration, distance, slideCount);
            // Scroll the slider
            this.scrollToOffset(nextOffset);
        }));
        return smoothScroll;
    }

    private interpolateScroll(scrollFrom: number, scrollTo: number, start: number, now: number, duration: number, distance: number, slideCount: number): number {
        // Animation progress between 0 and 1
        let progress = ((now - start) / duration);
        if(progress > 1) progress = 1;

        // Apply smooth progress function depending on number of slides 
        // let smoothProgress = (slideCount <= 1) ? Math.pow(progress, 1.2) : this.easeInOut(progress, (progress) => (1 - Math.sin(Math.acos(progress))));
        let smoothProgress = (slideCount <= 1) ? Math.pow(progress, 1.2) : this.easeInOut(progress);
        // let smoothProgress = this.easeInOut(progress, (progress) => Math.pow(progress, 2) * ((1.6 + 1) * progress - 1.6));

        // Calculate targetScroll
        let targetScroll = Math.round(scrollFrom + smoothProgress * distance);
        // Clamp target scroll to distance left
        let distanceLeft = Math.abs(targetScroll - scrollTo);
        return (distanceLeft <= 1) ? scrollTo : targetScroll;
    }

    private easeInOut(progress: number) {
        let sqt = progress * progress;
        return sqt / (2.0 * (sqt - progress) + 1.0);
    }

    private disableSnapping() {
        if(this.deviceService.isPlatformServer()) return;

        // Disable snapping while holding key down or auto fast scrolling
        // this.enableSnappingStop(false);
        // This is needed on android and chrome browsers (otherwise it won't animate the scroll (steps) between elements) 
        // Previously this would break ios, but since iOS ~15, disabling scroll-snap is also supported / needed on iOS Safari 
        this.slider.elementRef.nativeElement.style.setProperty('scroll-snap-type', "none");
    }

    public enableSnapping() {
        if(this.deviceService.isPlatformServer()) return;

        // Enable snapping to center of scroll item (when not fast sliding)
        // this.enableSnappingStop(true);
        let snapType = (this.orientation == 'horizontal') ? "x mandatory" : "y mandatory";
        this.slider.elementRef.nativeElement.style.setProperty('scroll-snap-type', snapType);
        // iOS workaround - scroll to current active after changing snapping (without this iOS will show weird flickering or nothing at all)
        // Use target idx (not active, as the active might not have changed yet)
        // this.scrolleToIdx(this.targetIdx);
    }

    private enableSnappingStop(enable: boolean) {
        if(this.deviceService.isPlatformServer()) return;

        let stopType = enable ? "always" : "normal";
        this.slider.elementRef.nativeElement.style.setProperty('--snap-stop-type', stopType);
    }

    public activateSnapToCenter(enable: boolean) {
        if(this.deviceService.isPlatformServer()) return;

        let snapPoint = enable ? "center" : "none";
        this.slider.elementRef.nativeElement.style.setProperty('--snap-align-type', snapPoint);
    }

    public trackSlideKey(idx: number, slide: Slide): string {
        if(!slide) return "";
        return slide.key;
    }

    public onTouchStart(ev: UIEvent): boolean {
        this.ongoingTouch = true;
        return true;
    }

    public onTouchEnd(ev: UIEvent): void {
        this.ongoingTouch = false;
        this.touchEndSubject.next(ev);
    }

    public onMouseDown(ev: MouseEvent, idx: number) {
        // Init mouse touch swiping  emulation
        this.ongoingTouch = true;
        this.touchTargetIdx = idx;
        this.touchStartTime = performance.now();
        this.touchStartOffset = this.slider.measureScrollOffset();
        this.disableSnapping();
    }

    public onMouseMove(ev: MouseEvent, idx: number) {
        // Swipe to cursor position
        if(!this.ongoingTouch) return;

        if(this.orientation == 'horizontal') {
            this.slider.elementRef.nativeElement.scrollLeft -= ev.movementX;
        }
        else {
            this.slider.elementRef.nativeElement.scrollTop -= ev.movementY;
        }
    }

    public onMouseUp(ev: MouseEvent) {
        // Dont do anything on mouse ev outside swiper
        if(!this.ongoingTouch) return;

        this.ongoingTouch = false;

        // If click are disabled, completely skip handling click and swipe events manually (by mouse or touch)
        if(!this.selectSlideOnClick) {
            // this.enableSnapping();
            return;
        }

        // Calculate touch time and speed
        let downTime = performance.now() - this.touchStartTime;
        let distance = this.slider.measureScrollOffset() - this.touchStartOffset;
        let direction = Math.sign(distance / downTime);
        let speed = Math.abs(distance / downTime);

        // Emulate swiping more than one slide if setting enabled
        // If scrolling slow, but very lon (more than a half slide), just scroll to active idx (no need to increase/decrease slide)
        let singleTarget = (Math.abs(distance) > this.slideSize * 0.6) ? this.activeIdx : this.activeIdx + direction;
        if(!this.emulateTouchMultiSwipe) {
            // Only allow single swipe if not multi is enabled by settings
            singleTarget = this.touchTargetIdx + direction;
        }

        // Scroll depending on touch speed
        this.targetIdx = speed > 0.1 ? singleTarget : this.activeIdx;

        if(this.emulateTouchMultiSwipe) {
            this.targetIdx = speed > 0.9 ? this.activeIdx + direction * 6 : this.targetIdx;
        }
        // Overwrite target idx if speed is low and based on input settings
        if(speed <= 0.1) this.targetIdx = this.touchTargetIdx;

        // Reset touch state
        this.touchStartTime = 0;
        this.touchStartOffset = 0;
        this.touchTargetIdx = -1;
        // Scroll to idx (and re-enable snapping afterwards)
        this.slideToIdx(this.targetIdx);
    }
}
